import { Button, FormHelperText, Typography } from '@mui/material'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import FormGroup from '@mui/material/FormGroup'
import { get } from 'lodash'
import Searchbar from '../Searchbar/Searchbar'
import { useState, useMemo } from 'react'

import { Aircraft } from 'types/graphql'

import DropdownFilter from '../DropdownFilter/DropdownFilter'

import AirplaneIcon from '@mui/icons-material/LocalAirportOutlined'

import ArrowDropDownIcon from '@mui/icons-material/KeyboardArrowDownOutlined'
import { AIRCRAFT_FILTER_QUERY } from './queries'
import useQuery from 'src/hooks/useQuery'
import useOrgName from 'src/hooks/useOrgName'

interface PlainFilterContentProps {
  gridView?: boolean
  gridCols?: number
  aircrafts: Aircraft[]
  aircraftsFilterData: any
  enableSearch?: boolean
  disableClearAll?: boolean
  error?: boolean
  helperText?: string
  setAircraftFilter: (aircraftId: string, checked: boolean) => void
  selectAllAircraftFilter?: () => void
  resetAircraftFilter: () => void
}
interface AircraftFilterProps extends PlainFilterContentProps {
  variant?: 'popover' | 'plain'
  dropdownStyles?: React.CSSProperties
  anchorRight?: boolean
  isFilterActive?: boolean
  open?: boolean
  onToggle?: (isOpen: boolean) => void
}

const PlainFilterContent: React.FC<PlainFilterContentProps> = ({
  gridView = false,
  disableClearAll = false,
  gridCols = 4,
  error,
  helperText,
  aircrafts,
  aircraftsFilterData,
  setAircraftFilter,
  resetAircraftFilter,
  selectAllAircraftFilter,
  enableSearch = false,
}) => {
  const [searchTerm, setSearchTerm] = useState('')
  const orgSlug = useOrgName()
  const { data } = useQuery(AIRCRAFT_FILTER_QUERY, {
    variables: {
      orgSlug: orgSlug,
    },
    skip: !orgSlug || !!aircrafts,
    fetchPolicy: 'cache-first'
  })

  const effectiveAircrafts = useMemo(() => {
    return aircrafts ?? data?.aircrafts ?? []
  }, [data])

  const filteredAircrafts = useMemo(() => {
    if (!searchTerm) return effectiveAircrafts

    return effectiveAircrafts.filter((aircraft) =>
      aircraft.tailNumber.toLowerCase().includes(searchTerm.toLowerCase())
    )
  }, [effectiveAircrafts, searchTerm])

  return (
    <>
      {enableSearch && (
        <Searchbar
          placeholder="Search aircrafts..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="mb-2 w-full"
        />
      )}
      <div className="flex w-full items-center justify-between">
        <Typography variant="caption" color={error ? 'error' : ''}>
          <Typography variant="body1">
            {error ? 'Aircrafts *' : 'Aircrafts'}
          </Typography>
        </Typography>
        <div className="flex">
          {selectAllAircraftFilter && (
            <Button
              variant="text"
              onClick={selectAllAircraftFilter}
              sx={{ textTransform: 'none', marginRight: 1 }}
            >
              Select all
            </Button>
          )}
          {!disableClearAll && (
            <Button
              variant="text"
              onClick={() => resetAircraftFilter()}
              sx={{ textTransform: 'none' }}
            >
              Clear all
            </Button>
          )}
        </div>
      </div>
      <FormGroup>
        <div
          className={gridView ? `grid grid-cols-${gridCols} ` : 'flex flex-col'}
        >
          {filteredAircrafts.map((aircraft) => {
            return (
              <FormControlLabel
                key={aircraft.tailNumber}
                control={
                  <Checkbox
                    key={aircraftsFilterData[aircraft.id] ?? aircraft.id}
                    checked={get(aircraftsFilterData, aircraft.id)}
                    onChange={(e) =>
                      setAircraftFilter(aircraft.id, e.target.checked)
                    }
                  />
                }
                label={aircraft.tailNumber}
              />
            )
          })}
        </div>
        {helperText && (
          <FormHelperText error={error} className="pb-2">
            {helperText}
          </FormHelperText>
        )}
      </FormGroup>
    </>
  )
}

const AircraftFilter: React.FC<AircraftFilterProps> = ({
  dropdownStyles,
  anchorRight = false,
  variant = 'popover',
  error,
  helperText,
  isFilterActive,
  open,
  onToggle,
  ...rest
}) => {
  if (variant === 'plain') {
    return (
      <PlainFilterContent error={error} helperText={helperText} {...rest} />
    )
  }

  return (
    <div className="z-50">
      <DropdownFilter
        label="AIRCRAFT"
        error={error}
        dropdownStyles={dropdownStyles}
        anchorRight={anchorRight}
        startIcon={<AirplaneIcon />}
        endIcon={<ArrowDropDownIcon />}
        enablePrimaryColor={isFilterActive}
        open={open}
        onToggle={onToggle}
      >
        <PlainFilterContent {...rest} />
      </DropdownFilter>
      {helperText && (
        <FormHelperText error={error} className="py-1">
          {helperText}
        </FormHelperText>
      )}
    </div>
  )
}

export default AircraftFilter
